import React, { useState } from 'react';
import { PlusIcon, PencilSquareIcon, TrashIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { useQuery, useMutation, usePagination } from '../../hooks';
import { bannersAPI } from '../../services/api';
import {
  Card, SectionHeader, Button, Badge, StatusBadge,
  Modal, Input, Select, Textarea, Pagination, LoadingPage,
  EmptyState, ConfirmDialog,
} from '../../components/common/UI';
import { useForm } from 'react-hook-form';
import { format } from 'date-fns';

const POSITIONS = ['home_hero','home_top', 'home_middle', 'home_bottom', 'category', 'sidebar'];

// ── Toggle Field ──────────────────────────────────────
const ToggleField = ({ label, hint, checked, onChange }) => (
  <div className="flex items-center justify-between py-2">
    <div>
      <p className="text-sm font-medium text-surface-800">{label}</p>
      {hint && <p className="text-xs text-surface-400">{hint}</p>}
    </div>
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0
        ${checked ? 'bg-surface-900' : 'bg-surface-200'}`}
    >
      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform
        ${checked ? 'translate-x-4' : 'translate-x-0.5'}`} />
    </button>
  </div>
);

// ── Banner Form ───────────────────────────────────────
const BannerForm = ({ banner, onSubmit, loading }) => {
  const [isActive, setIsActive] = useState(
    banner ? Boolean(banner.is_active) : true
  );

  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: banner
      ? { ...banner }
      : { position: 'home_hero', sort_order: 0 },
  });

  const handleFormSubmit = (data) => {
    const formData = new FormData();
    formData.append('title', data.title);
    
    if (data.image_file && data.image_file[0]) {
      formData.append('image', data.image_file[0]);
    }
    
    if (data.link_url) formData.append('link_url', data.link_url);
    if (data.description) formData.append('description', data.description);
    formData.append('position', data.position);
    formData.append('sort_order', data.sort_order);
    if (data.valid_from) formData.append('valid_from', data.valid_from);
    if (data.valid_until) formData.append('valid_until', data.valid_until);
    formData.append('is_active', isActive ? 1 : 0);

    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <Input
        label="Title *"
        error={errors.title?.message}
        {...register('title', { required: 'Title is required' })}
      />

      <Input
        label={banner?.image_url ? "Image File (Leave blank to keep existing)" : "Image File *"}
        type="file"
        accept="image/*"
        error={errors.image_file?.message}
        {...register('image_file', { required: banner?.image_url ? false : 'Image file is required' })}
      />

      <Input
        label="Link URL"
        placeholder="https://... (optional)"
        {...register('link_url')}
      />

      <Textarea
        label="Description *"
        rows={2}
        error={errors.description?.message}
        {...register('description', { required: 'Description is required' })}
      />

      <div className="grid grid-cols-2 gap-3">
        <Select label="Position" {...register('position')}>
          {POSITIONS.map(p => (
            <option key={p} value={p}>{p}</option>
          ))}
        </Select>
        <Input
          label="Sort Order"
          type="number"
          min="0"
          {...register('sort_order')}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Input
          label="Start Date"
          type="datetime-local"
          {...register('valid_from')}
        />
        <Input
          label="End Date"
          type="datetime-local"
          {...register('valid_until')}
        />
      </div>

      <ToggleField
        label="Active"
        hint="Visible to users"
        checked={isActive}
        onChange={setIsActive}
      />

      <Button type="submit" variant="primary" size="lg" className="w-full" loading={loading}>
        {banner ? 'Save Changes' : 'Create Banner'}
      </Button>
    </form>
  );
};

// ── Main Page ─────────────────────────────────────────
export default function BannersPage({ business }) {
  const [positionFilter, setPositionFilter] = useState('');
  const [showModal, setShowModal]           = useState(false);
  const [editBanner, setEditBanner]         = useState(null);
  const [deleteTarget, setDeleteTarget]     = useState(null);

  const { data, loading, refetch } = useQuery(
    () => bannersAPI.adminGetAll({ position: positionFilter || undefined, business_id: business?.id }),
    [positionFilter, business?.id],
    { enabled: !!business?.id }
  );

  const { mutate: createBanner, loading: creating } = useMutation(
    (d) => bannersAPI.create(d),
    { successMsg: 'Banner created', onSuccess: () => { refetch(); setShowModal(false); } }
  );

  const { mutate: updateBanner, loading: updating } = useMutation(
    (d) => bannersAPI.update(editBanner?.id, d),
    { successMsg: 'Banner updated', onSuccess: () => { refetch(); setEditBanner(null); } }
  );

  const { mutate: toggleBanner } = useMutation(
    ({ id, is_active }) => bannersAPI.toggleActive(id, { is_active }),
    { successMsg: 'Banner updated', onSuccess: () => refetch() }
  );

  const { mutate: deleteBanner, loading: deleting } = useMutation(
    () => bannersAPI.delete(deleteTarget?.id),
    { successMsg: 'Banner deleted', onSuccess: () => { refetch(); setDeleteTarget(null); } }
  );

  const banners = data?.data || data || [];
  const total   = Array.isArray(banners) ? banners.length : 0;

  return (
    <div className="animate-in space-y-5">
      <SectionHeader
        title="Banners"
        subtitle={`${total} banners`}
        action={
          <Button variant="primary" size="sm" icon={PlusIcon} onClick={() => setShowModal(true)}>
            Add Banner
          </Button>
        }
      />

      <Card>
        {/* ── Filter ── */}
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <select
            className="input w-48"
            value={positionFilter}
            onChange={e => setPositionFilter(e.target.value)}
          >
            <option value="">All Positions</option>
            {POSITIONS.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>

        {/* ── Table ── */}
        {loading ? <LoadingPage /> : banners.length === 0 ? (
          <EmptyState
            icon={PhotoIcon}
            title="No banners found"
            action={
              <Button variant="primary" size="sm" icon={PlusIcon} onClick={() => setShowModal(true)}>
                Add First Banner
              </Button>
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Banner</th>
                  <th>Position</th>
                  <th>Sort</th>
                  <th>Dates</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {banners.map(b => (
                  <tr key={b.id}>
                    {/* ── Image + Title ── */}
                    <td>
                      <div className="flex items-center gap-3">
                        {b.image_url
                          ? <img
                              src={b.image_url}
                              alt={b.title}
                              className="h-12 w-20 rounded-lg object-cover flex-shrink-0"
                            />
                          : <div className="flex h-12 w-20 flex-shrink-0 items-center justify-center rounded-lg bg-surface-100">
                              <PhotoIcon className="h-5 w-5 text-surface-400" />
                            </div>
                        }
                        <div>
                          <p className="font-medium text-surface-900 max-w-[180px] truncate">{b.title}</p>
                          {b.link_url && (
                            <a
                              href={b.link_url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-xs text-brand-500 truncate max-w-[180px] block hover:underline"
                            >
                              {b.link_url}
                            </a>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* ── Position ── */}
                    <td>
                      <Badge variant="gray">{b.position}</Badge>
                    </td>

                    {/* ── Sort Order ── */}
                    <td>
                      <span className="font-mono text-sm text-surface-500">
                        {b.sort_order ?? 0}
                      </span>
                    </td>

                    {/* ── Dates ── */}
                    <td>
                      <div className="text-xs text-surface-400 space-y-0.5">
                        {b.valid_from && (
                          <p>From: {format(new Date(b.valid_from), 'dd MMM yy')}</p>
                        )}
                        {b.valid_until && (
                          <p>To: {format(new Date(b.valid_until), 'dd MMM yy')}</p>
                        )}
                        {!b.valid_from && !b.valid_until && <p>Always</p>}
                      </div>
                    </td>

                    {/* ── Status toggle ── */}
                    <td>
                      <button
                        onClick={() => toggleBanner({ id: b.id, is_active: b.is_active ? 0 : 1 })}
                        className="focus:outline-none"
                        title="Toggle active"
                      >
                        <StatusBadge status={b.is_active ? 'active' : 'suspended'} />
                      </button>
                    </td>

                    {/* ── Actions ── */}
                    <td>
                      <div className="flex items-center gap-1">
                        <button
                          className="btn btn-ghost p-1.5 rounded-lg"
                          onClick={() => setEditBanner(b)}
                          title="Edit"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                        <button
                          className="btn btn-ghost p-1.5 rounded-lg text-danger-500"
                          onClick={() => setDeleteTarget(b)}
                          title="Delete"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ── Add Modal ── */}
      <Modal open={showModal} onClose={() => setShowModal(false)} title="Add Banner" size="md">
        <BannerForm onSubmit={createBanner} loading={creating} />
      </Modal>

      {/* ── Edit Modal ── */}
      <Modal open={!!editBanner} onClose={() => setEditBanner(null)} title="Edit Banner" size="md">
        <BannerForm banner={editBanner} onSubmit={updateBanner} loading={updating} />
      </Modal>

      {/* ── Delete Confirm ── */}
      <ConfirmDialog
        open={!!deleteTarget} danger
        title="Delete Banner"
        message={`Delete "${deleteTarget?.title}"? This cannot be undone.`}
        confirmText="Delete" loading={deleting}
        onClose={() => setDeleteTarget(null)} onConfirm={deleteBanner}
      />
    </div>
  );
}